import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  ClickAwayListener,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import { SymbolV2, useGetQuotesV2 } from "@bn/api/apiv1";
import InfoIcon from "@icons/myicons/info.svg?react";
import LockIcon from "@icons/myicons/lock.svg?react";
import { STOCK_INDEXES } from "@constants";
import { TypographyWithInfoIcon } from "@components/TypographyWithInfoIcon";
import { FakeSearchField } from "@components/common/layouts/AppSidebar";
import { useMyself } from "@api";
import { ComparisonValue } from "../types";

const iconSize = { width: 20, height: 20 };

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  textDecoration: "underline",
  color: theme.palette.primary.main,
  padding: theme.spacing(2.7, 0),
  "&:not(:last-child)": {
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
  },

  "&:hover": {
    textDecoration: "underline",
    color: theme.palette.primary.main,
  },
}));

const Wrapper = styled.div`
  width: 100%;
  .MuiList-root {
    padding: 0 auto;
  }
`;

export interface SelectCompareProps {
  value?: ComparisonValue | null;
  onChange: (ticker?: ComparisonValue) => void;
  disabled?: boolean;
}

export const SelectCompare = (props: SelectCompareProps) => {
  const { value, disabled, onChange } = props;
  const { data: myself } = useMyself();
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolV2>();
  const [isActive, setIsActive] = useState<boolean>();
  const isPlusUser = myself?.isUserPlus;

  const buttonRef = useRef<HTMLDivElement>(null);

  const { data: quoteData } = useGetQuotesV2(
    {
      securityId: [selectedSymbol?.id!],
      quoteProvider: selectedSymbol?.quoteProvider!,
    },
    {
      enabled: Boolean(selectedSymbol),
    }
  );
  const quote = quoteData?.quotes?.[0];

  useEffect(() => {
    if (!selectedSymbol) return;
    onChange({
      label: selectedSymbol?.name!,
      value: quote?.tickerSymbol!,
      quoteProvider: selectedSymbol?.quoteProvider!,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, selectedSymbol]);

  const handleComparedValueChange = (symbol?: SymbolV2) => {
    setIsOpen(false);
    setSelectedSymbol(symbol);
    if (!symbol) onChange(undefined);
  };

  const handleComparedTickerChange = (comparedTicker?: ComparisonValue) => {
    setIsOpen(false);
    onChange(comparedTicker);
  };

  useEffect(() => {
    const handleFocusChange = () => {
      setIsActive(buttonRef.current?.contains(document.activeElement));
    };

    document.addEventListener("focus", handleFocusChange, true);
    return () => {
      document.removeEventListener("focus", handleFocusChange, true);
    };
  }, []);

  return (
    <>
      <FormControl fullWidth>
        <InputLabel
          color="primary"
          sx={{
            pointerEvents: "initial",
          }}
        >
          <Box display="flex" gap={1} alignItems="center">
            {!isPlusUser && (
              <LockIcon {...iconSize} color={theme.palette.icon.tertiary} />
            )}
            {isPlusUser ? (
              <TypographyWithInfoIcon
                variant="body3"
                icon={
                  <InfoIcon {...iconSize} color={theme.palette.icon.tertiary} />
                }
                tooltipText={t(
                  "dashboardPage.dashboard.performance.compareTooltip"
                )}
              >
                {t("dashboardPage.dashboard.performance.compare")}
              </TypographyWithInfoIcon>
            ) : (
              <Typography variant="body3">
                {t("dashboardPage.dashboard.performance.compare")}
              </Typography>
            )}
          </Box>
        </InputLabel>
        <Wrapper>
          <Select
            ref={buttonRef}
            open={isOpen}
            displayEmpty
            disabled={disabled || !isPlusUser}
            onClick={(event) => {
              if (
                (event.target as HTMLElement).closest(".MuiSelect-select") &&
                !(event.target as HTMLElement).closest(".clear") &&
                !disabled &&
                isPlusUser
              )
                setIsOpen(true);
            }}
            sx={{ width: "100%" }}
            MenuProps={{
              disablePortal: true,
            }}
            renderValue={() =>
              value?.label ? (
                <Box
                  position="relative"
                  pr={15}
                  onMouseEnter={() => setIsActive(true)}
                  onMouseLeave={() =>
                    setIsActive(
                      buttonRef.current?.contains(document.activeElement)
                    )
                  }
                >
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    lineHeight="20px"
                    noWrap
                  >
                    {value.label}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      handleComparedValueChange(undefined);
                      handleComparedTickerChange(undefined);
                    }}
                    size="small"
                    className="clear"
                    sx={{
                      position: "absolute",
                      right: "0",
                      top: "-7px",
                      "& svg": {
                        width: "20px",
                        height: "20px",
                      },
                    }}
                  >
                    {isActive && <CloseIcon />}
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="body3"
                  color={!isPlusUser ? "text.disabled" : "textSecondary"}
                  fontWeight={400}
                  lineHeight="20px"
                >
                  {t("dashboardPage.dashboard.performance.compareWith")}
                </Typography>
              )
            }
          >
            <Box>
              <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <Box sx={{ padding: "16px 16px 0" }}>
                  <Box mb={3}>
                    <FakeSearchField
                      collapsed={false}
                      placeholder="dashboardPage.dashboard.performance.selectCompare.placeholder"
                      bgColor="transparent"
                      onSymbolSelect={handleComparedValueChange}
                    />
                  </Box>
                  <Typography
                    variant="overline"
                    color="textSecondary"
                    sx={{
                      textTransform: "uppercase",
                    }}
                  >
                    {t("dashboardPage.dashboard.performance.compareWith")}
                  </Typography>
                  <Box>
                    {STOCK_INDEXES.map((comparisonValue) => (
                      <MenuItemStyled
                        value={comparisonValue.value}
                        key={comparisonValue.value}
                        selected={comparisonValue.value === value?.value}
                        onClick={() =>
                          handleComparedTickerChange(comparisonValue)
                        }
                      >
                        <Typography variant="caption" px={2}>
                          {comparisonValue.label}
                        </Typography>
                      </MenuItemStyled>
                    ))}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Box>
          </Select>
        </Wrapper>
      </FormControl>
    </>
  );
};
